// frontend/src/components/info-alert.tsx
// Reusable tinted info alert with an optional CTA button and an optional
// remove (X). Consolidates the blue-info-banner pattern that was hand-rolled
// across the app (org usage banner, onboarding hints, empty states). Read
// DESIGN.md before restyling — the blue/amber/destructive tones are the
// established info/warning/error palette.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const TONES = {
  info: { box: 'border-blue-500/30 bg-blue-500/5', accent: 'text-blue-500' },
  amber: { box: 'border-amber-500/30 bg-amber-500/5', accent: 'text-amber-500' },
  destructive: { box: 'border-destructive/30 bg-destructive/5', accent: 'text-destructive' },
} as const;

export interface InfoAlertAction {
  label: string;
  /** Internal route — rendered as a react-router Link. */
  to?: string;
  /** Click handler used when `to` is not provided. */
  onClick?: () => void;
}

export interface InfoAlertProps {
  children: React.ReactNode;
  /** Visual tone. info = blue (default), amber = warning, destructive = error. */
  tone?: keyof typeof TONES;
  /** Leading icon; defaults to the Info glyph. */
  icon?: LucideIcon;
  /** Optional CTA button rendered under the message. */
  action?: InfoAlertAction;
  /** Custom action node (e.g. a dialog trigger). Wins over `action`. */
  actionSlot?: React.ReactNode;
  /**
   * Make the alert removable. Provide `dismissKey` to persist the removal in
   * localStorage (stays hidden across reloads), or `onDismiss` to control the
   * hidden state from the parent. With neither, the alert is not removable.
   */
  dismissKey?: string;
  onDismiss?: () => void;
}

function readDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * Tinted alert with a leading icon, message, optional CTA button, and optional
 * remove control. When `dismissKey` is set the remove is persisted to
 * localStorage so the alert stays hidden after a reload.
 */
export function InfoAlert({
  children,
  tone = 'info',
  icon: Icon = Info,
  action,
  actionSlot,
  dismissKey,
  onDismiss,
}: InfoAlertProps) {
  const navigate = useNavigate();
  const [removed, setRemoved] = useState(() => (dismissKey ? readDismissed(dismissKey) : false));
  if (removed) return null;

  const removable = Boolean(dismissKey || onDismiss);
  const t = TONES[tone];

  function handleDismiss() {
    if (dismissKey) {
      try {
        localStorage.setItem(dismissKey, '1');
      } catch {
        // Best-effort; the alert still hides for this session.
      }
    }
    setRemoved(true);
    onDismiss?.();
  }

  return (
    <Alert className={cn('relative', t.box, removable && 'pr-10')}>
      <Icon className={`size-4 ${t.accent}`} />
      <AlertDescription className="space-y-3">
        <p>{children}</p>
        {actionSlot !== undefined
          ? actionSlot
          : (action && (
            <Button
              size="sm"
              onClick={action.to ? () => navigate(action.to as string) : action.onClick}
            >
              {action.label}
            </Button>
          ))}
      </AlertDescription>
      {removable && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <X className="size-4" />
        </button>
      )}
    </Alert>
  );
}
